import { Col, Form, Input, Row, TableColumnsType } from "antd";
import dayjs from "dayjs";
import * as React from "react";
import isEqual from "react-fast-compare";

import { CoreQrReportItem, PaymentGateway } from "src/types";

import { usePartnerReport, UsePartnerReportFilters } from "src/hooks/usePartnerReport";
import { getColumnFilters } from "src/utils/table";

import { DatePicker, SearchButton, Table } from "src/components";
import DeskPanel from "src/components/DeskPanel/DesktPanel";
import { AmountColumn } from "src/components/Table";

interface IPartnerReportPageProps {}

const DISPLAY_DATE_TIME_FORMAT = "DD/MM/YYYY HH:mm";
// Channels shown on this page — the provider column filter is built from
// whatever these calls return, so add a partner here to make it filterable.
const PARTNER_CHANNELS: string[] = [PaymentGateway.SECUREPAY, PaymentGateway.P2PAY];
const APP_NAME = "PIGSPIN";

export const PartnerReportPage: React.FunctionComponent<IPartnerReportPageProps> = (_) => {
  const [fetchFilters, setFetchFilters] = React.useState<UsePartnerReportFilters>({
    channels: PARTNER_CHANNELS,
    appName: APP_NAME,
    startTime: dayjs().startOf("day").toDate(),
    endTime: dayjs().endOf("day").toDate(),
  });

  const [form] = Form.useForm();
  const { data, isFetching, refetch } = usePartnerReport(fetchFilters);

  const getDisplayReportResults = () =>
    data?.results.map((rawReport) => ({
      ...rawReport,
      createdAt: dayjs.utc(rawReport.createdAt).local().format(DISPLAY_DATE_TIME_FORMAT),
      updatedAt: dayjs.utc(rawReport.updatedAt).local().format(DISPLAY_DATE_TIME_FORMAT),
    }));

  const displayReportResults = React.useMemo(getDisplayReportResults, [data]);

  const getAllColumns = (): TableColumnsType<CoreQrReportItem> => {
    return [
      {
        className: "table-cell",
        title: "รหัสลูกค้า",
        dataIndex: "customerCode",
        key: "customerCode",
        filterSearch: true,
        filters: getColumnFilters("customerCode", displayReportResults || []),
        onFilter: (value, row) => row.customerCode?.includes(value.toString()) || false,
      },
      {
        className: "table-cell",
        title: "จำนวนเงิน",
        dataIndex: "amount",
        key: "amount",
        render: (value) => <AmountColumn amount={value} />,
      },
      {
        className: "table-cell",
        title: "ผู้ให้บริการ",
        dataIndex: "provider",
        key: "provider",
        // Fixed list so both partners stay selectable on a day one of them
        // has no transactions.
        filters: PARTNER_CHANNELS.map((channel) => ({ text: channel, value: channel })),
        onFilter: (value, row) => row.provider === value.toString(),
      },
      {
        className: "table-cell",
        title: "สถานะ",
        dataIndex: "status",
        key: "status",
        filterSearch: true,
        filters: getColumnFilters("status", displayReportResults || []),
        onFilter: (value, row) => row.status?.includes(value.toString()) || false,
      },
      {
        className: "table-cell",
        title: "Ref ID",
        dataIndex: "refId",
        key: "refId",
      },
      {
        className: "table-cell",
        title: "Provider Ref ID",
        dataIndex: "providerRefId",
        key: "providerRefId",
      },
      {
        className: "table-cell",
        title: "วันเวลาสร้าง",
        dataIndex: "createdAt",
        key: "createdAt",
        filterSearch: true,
        filters: getColumnFilters("createdAt", displayReportResults || []),
        onFilter: (value, row) => row.createdAt?.includes(value.toString()) || false,
      },
      {
        className: "table-cell",
        title: "วันเวลาอัพเดตล่าสุด",
        dataIndex: "updatedAt",
        key: "updatedAt",
        filterSearch: true,
        filters: getColumnFilters("updatedAt", displayReportResults || []),
        onFilter: (value, row) => row.updatedAt?.includes(value.toString()) || false,
      },
    ];
  };

  const onFinish = (value: any) => {
    const newFetchFilters: UsePartnerReportFilters = {
      searchCustomer: value?.search,
      startTime: value?.datetimeRange[0].toDate(),
      endTime: value?.datetimeRange[1].toDate(),
      channels: PARTNER_CHANNELS,
      appName: APP_NAME,
    };
    if (isEqual(fetchFilters, newFetchFilters)) {
      refetch();
    } else {
      setFetchFilters(newFetchFilters);
    }
  };

  return (
    <div className="PartnerReportPage">
      <Form
        form={form}
        onFinish={onFinish}
        layout="vertical"
        initialValues={{
          datetimeRange: [dayjs().startOf("day"), dayjs().endOf("day")],
          search: undefined,
        }}
      >
        <Row gutter={8} wrap={false} align="bottom">
          <Col flex="auto">
            <Form.Item name="search" label="รหัสลูกค้า">
              <Input size="large" placeholder="ใส่รหัสลูกค้า" allowClear />
            </Form.Item>
          </Col>
          <Col>
            <Form.Item
              name="datetimeRange"
              label={
                <Row style={{ width: "340px" }}>
                  <Col span={12}>{"วันเวลาเริ่มต้น"}</Col>
                  <Col span={12}>{"วันเวลาสิ้นสุด"}</Col>
                </Row>
              }
            >
              <DatePicker.RangePicker
                size="large"
                allowClear={false}
                format="DD/MM/YYYY HH:mm"
                showTime={{ format: "HH:mm" }}
                style={{ width: "340px" }}
              />
            </Form.Item>
          </Col>
          <Col>
            <Form.Item shouldUpdate>
              {() => (
                <SearchButton
                  size="large"
                  htmlType="submit"
                  disabled={!form.getFieldValue("datetimeRange") || isFetching}
                />
              )}
            </Form.Item>
          </Col>
        </Row>
      </Form>
      <DeskPanel
        body={
          <Table
            className="main-table"
            rowKey="refId"
            dataSource={displayReportResults}
            columns={getAllColumns()}
            loading={isFetching}
            size="small"
            bordered
          />
        }
      />
    </div>
  );
};

export default PartnerReportPage;
